"use client";

import React from "react";
import Image from "next/image";
import { PortableText } from "@portabletext/react";
import { PortableTextBlock } from "sanity";

import { GROQButton, GROQImage } from "@/sanity/lib/definitions";
import { IOptInExpectItem } from "@/sanity/pages/PageOptInTemplate";
import { customRichTextComponent } from "@/sanity/sanityPortableText";
import { CTAButton } from "@/shadcn/ui/button";
import RiveAnimation, { Fit } from "@/components/global/RiveAnimation";
import { FadeIn } from "@/components/global/utils/FadeIn";
import { PipeChip } from "../home/_shared";

import { CheckmarkIcon } from "./_icons";

interface ExpectSectionProps {
  chip?: string;
  title?: PortableTextBlock[];
  image?: GROQImage;
  items?: IOptInExpectItem[];
  bannerText?: PortableTextBlock[];
  bannerCTA?: GROQButton;
}

// "What's inside" (Figma 5143:2161 desktop, 5143:2493 mobile) — white
// section: chip + serif headline + body, then the square visual (with the
// design's fading side washes) beside a hairline-separated checklist of
// title/text pairs, closing on a translucent banner row with a CTA. The visual
// is the Sanity image; without one the shared WCE Rive loop fills the slot.
export default function ExpectSection({ chip, title, image, items, bannerText, bannerCTA }: ExpectSectionProps) {
  const list = items ?? [];

  const heading = ({ children }: { children?: React.ReactNode }) => (
    <h2 className="mx-auto mb-0 text-center font-news text-[4.5rem] leading-[1.1] font-normal tracking-[-0.125rem] text-black lg:max-w-[92.75rem] lg:text-[6rem] lg:tracking-[-0.21rem]">
      {children}
    </h2>
  );

  return (
    <section className="flex w-full flex-col items-center gap-[3rem] bg-white px-[2rem] py-[7rem] text-black lg:gap-[5rem] lg:px-[45rem] lg:py-[12.5rem]">
      {(chip || title) && (
        <div className="flex w-[min(131.25rem,100%)] flex-col items-center gap-[1rem] text-center lg:gap-[2.5rem]">
          <div className="flex w-full flex-col items-center">
            {chip && (
              <FadeIn delay={0}>
                <PipeChip color="#000000">{chip}</PipeChip>
              </FadeIn>
            )}

            {title && (
              <FadeIn
                delay={0.1}
                className="flex w-full flex-col items-center gap-[1rem] [text-wrap:balance]">
                <PortableText
                  value={title}
                  components={customRichTextComponent({
                    block: {
                      h1: heading,
                      h2: heading,
                      normal: ({ children }) => (
                        <p className="mx-auto mb-0 text-center font-geist text-[2.25rem] leading-[1.5] text-black/60 lg:max-w-[53rem] lg:font-medium">
                          {children}
                        </p>
                      ),
                    },
                  })}
                />
              </FadeIn>
            )}
          </div>
        </div>
      )}

      <div className="flex w-[min(155rem,100%)] flex-col items-start gap-[3rem] lg:items-center lg:gap-[4rem]">
        <div className="flex w-full flex-col items-center gap-[3rem] lg:flex-row lg:items-center lg:gap-[8rem]">
          <FadeIn
            delay={0}
            className="relative aspect-square w-full shrink-0 overflow-clip rounded-[1rem] lg:w-[77.0625rem] lg:rounded-[1.567rem]">
            <RiveAnimation
              src="/home/wce.riv"
              fit={Fit.Cover}
              className="absolute inset-0 h-full w-full"
            />

            {/* Side washes — the design fades the visual's edges into the page. */}
            <div
              aria-hidden
              className="pointer-events-none absolute inset-y-0 left-0 w-[30%] bg-gradient-to-r from-[rgba(238,238,238,0.5)] to-[rgba(238,238,238,0)]"
            />
            <div
              aria-hidden
              className="pointer-events-none absolute inset-y-0 right-0 w-[30%] bg-gradient-to-l from-[rgba(238,238,238,0.39)] to-[rgba(238,238,238,0)]"
            />
          </FadeIn>

          {list.length > 0 && (
            <div className="flex w-full flex-col items-start gap-[2rem]">
              {list.map((item, i) => (
                <React.Fragment key={item._key ?? i}>
                  {i > 0 && (
                    <hr
                      aria-hidden
                      className="m-0 w-full border-0 border-t border-black/10"
                    />
                  )}

                  <FadeIn
                    delay={0.05 * i}
                    className="flex w-full items-start gap-[2.25rem]">
                    <span className="flex h-[4.41rem] w-[4.41rem] shrink-0 items-center justify-center rounded-[0.375rem] border border-white/[0.06] bg-black text-white">
                      <CheckmarkIcon />
                    </span>

                    <div className="flex flex-1 flex-col items-start justify-center gap-[1rem]">
                      {item.title && (
                        <p className="mb-0 w-full font-geist text-[2.45rem] leading-[1.4] tracking-[-0.0245rem] text-black/80">
                          {item.title}
                        </p>
                      )}
                      {item.text && (
                        <p className="mb-0 w-full font-geist text-[2rem] leading-[1.4] tracking-[-0.02rem] text-black/60">
                          {item.text}
                        </p>
                      )}
                    </div>
                  </FadeIn>
                </React.Fragment>
              ))}
            </div>
          )}
        </div>

        {(bannerText || bannerCTA?.cta) && (
          <FadeIn
            delay={0}
            className="flex w-full flex-col items-center justify-between gap-[3rem] rounded-[1.25rem] border border-black/[0.06] bg-black/5 px-[2.5rem] py-[2.5rem] backdrop-blur-[4.7rem] lg:flex-row lg:pl-[3.75rem]">
            {bannerText && (
              <PortableText
                value={bannerText}
                components={customRichTextComponent({
                  block: {
                    normal: ({ children }) => (
                      <p className="mb-0 max-w-[31.25rem] text-center font-geist text-[2.5rem] leading-[1.5] tracking-[-0.125rem] text-black/70 lg:max-w-[53.25rem] lg:text-left lg:leading-[1.3]">
                        {children}
                      </p>
                    ),
                  },
                  marks: {
                    em: ({ children }) => (
                      <em className="italic text-black underline decoration-from-font [text-underline-offset:from-font] [text-decoration-skip-ink:none]">
                        {children}
                      </em>
                    ),
                  },
                })}
              />
            )}

            {bannerCTA?.cta && (
              <div className="w-full lg:w-auto">
                <CTAButton data={bannerCTA} />
              </div>
            )}
          </FadeIn>
        )}
      </div>
    </section>
  );
}
